import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RotateCcw, Shuffle, Flame, CalendarDays, ArrowRight, ChevronDown, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { NovaOrb } from "../components/NovaOrb";
import { BottomNav } from "../components/BottomNav";
import { api, formatApiError } from "../lib/api";

const GRID_STYLE = {
  completed_full: "bg-kaizen-violet border-kaizen-violet",
  completed_floor: "bg-kaizen-violet/50 border-kaizen-violet ring-2 ring-kaizen-violet/30",
  today: "bg-transparent border-kaizen-violetSoft/70 animate-pulse",
  neutral: "bg-transparent border-white/10",
};

const ConsistencyRingLarge = ({ pct = 0 }) => {
  const r = 64;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative w-[150px] h-[150px] flex items-center justify-center" data-testid="progress-ring">
      <svg width="150" height="150" viewBox="0 0 150 150">
        <circle cx="75" cy="75" r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="10" />
        <circle
          cx="75" cy="75" r={r} fill="none" stroke="#B37FE8" strokeWidth="10" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (c * pct) / 100}
          transform="rotate(-90 75 75)"
          style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.16,1,0.3,1)", filter: "drop-shadow(0 0 10px rgba(179,127,232,0.55))" }}
        />
      </svg>
      <span className="absolute text-2xl font-semibold text-kaizen-text">{pct}%</span>
    </div>
  );
};

const WeekSessionRow = ({ session, idx, expanded, onToggle }) => (
  <div className="border-b border-white/[0.06] last:border-0" data-testid={`week-session-row-${idx}`}>
    <button
      onClick={() => onToggle(idx)}
      data-testid={`week-session-toggle-${idx}`}
      className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className={`flex items-center justify-center w-5 h-5 rounded-full border shrink-0 ${
            session.completed ? "bg-kaizen-violet border-kaizen-violet" : "border-white/15"
          }`}
        >
          {session.completed && <Check size={11} className="text-[#140A24]" />}
        </span>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-kaizen-dim2">{session.day_label}</p>
          <p className="text-sm text-kaizen-text truncate">{session.muscle_focus}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 text-kaizen-dim">
        <span className="flex items-center gap-1 text-xs">
          <Clock size={11} /> {session.duration_min}m
        </span>
        <ChevronDown size={14} className={`transition-transform duration-300 ${expanded ? "rotate-180 text-kaizen-violetSoft" : ""}`} />
      </div>
    </button>
    {expanded && (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        className="pb-4"
        data-testid={`week-session-exercises-${idx}`}
      >
        {(session.exercises || []).map((ex, i) => (
          <div key={i} className="flex items-center justify-between py-1.5 text-xs">
            <span className="text-kaizen-text/90">{ex.name}</span>
            <span className="text-kaizen-dim">{ex.sets} × {ex.reps} · {ex.rest_sec}s rest</span>
          </div>
        ))}
      </motion.div>
    )}
  </div>
);

export default function Progress() {
  const [data, setData] = useState(null);
  const [reflowing, setReflowing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [rebuildNote, setRebuildNote] = useState("");

  const load = () => api.get("/progress").then(({ data: d }) => setData(d)).catch(() => setData({ error: true }));

  useEffect(() => {
    load();
  }, []);

  const runAction = async (endpoint, setLoading, note) => {
    setLoading(true);
    setRebuildNote("");
    try {
      await api.post(`/progress/${endpoint}`);
      await load();
      setRebuildNote(note);
      setTimeout(() => setRebuildNote(""), 4000);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-kaizen-bg flex items-center justify-center">
        <NovaOrb size={90} />
      </div>
    );
  }

  const day1 = data.milestones?.day1;
  const current = data.milestones?.current;

  return (
    <div className="min-h-screen bg-kaizen-bg text-kaizen-text pb-28" data-testid="progress-page">
      <div className="max-w-md mx-auto px-5 pt-8">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-display text-2xl">Your progress</p>
            <p className="text-sm text-kaizen-dim mt-1">Any showing-up counts — floor days included.</p>
          </div>
          <span className="text-xs px-3 py-1.5 rounded-full border border-kaizen-violet/40 text-kaizen-violetSoft shrink-0" data-testid="week-number-badge">
            Week {data.week_number ?? 1}
          </span>
        </div>

        <div className="glass-card rounded-3xl p-6 mt-6 flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-kaizen-dim">Consistency</p>
            <p className="text-sm text-kaizen-dim mt-2 max-w-[140px]">Counting every day you showed up, since day 1.</p>
          </div>
          <ConsistencyRingLarge pct={data.consistency_pct ?? 0} />
        </div>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <div className="glass-card rounded-3xl p-5" data-testid="current-streak-card">
            <div className="flex items-center gap-1.5 text-kaizen-dim">
              <Flame size={13} />
              <p className="text-xs uppercase tracking-widest">Streak</p>
            </div>
            <p className="font-display text-3xl text-kaizen-violetSoft mt-2">{data.current_streak ?? 0}</p>
            <p className="text-xs text-kaizen-dim mt-1">days, forgiveness included</p>
          </div>
          <div className="glass-card rounded-3xl p-5" data-testid="floor-days-summary">
            <div className="flex items-center gap-1.5 text-kaizen-dim">
              <RotateCcw size={13} />
              <p className="text-xs uppercase tracking-widest">Floor days</p>
            </div>
            <p className="font-display text-3xl text-kaizen-violetSoft mt-2">{data.floor_days_count ?? 0}</p>
            <p className="text-xs text-kaizen-dim mt-1">full credit, every time</p>
          </div>
        </div>

        <div className="glass-card rounded-3xl p-6 mt-4" data-testid="fourteen-day-grid">
          <div className="flex items-center gap-1.5 text-kaizen-dim mb-4">
            <CalendarDays size={13} />
            <p className="text-xs uppercase tracking-widest">Last 14 days</p>
          </div>
          <div className="grid grid-cols-7 gap-y-4">
            {(data.grid || []).map((d) => (
              <div key={d.date} className="flex flex-col items-center gap-2" data-testid={`grid-day-${d.date}`}>
                <span className="text-[10px] text-kaizen-dim2">{d.day_name.slice(0, 2)}</span>
                <span className={`w-4 h-4 rounded-full border ${GRID_STYLE[d.status] || GRID_STYLE.neutral}`} />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-5 text-[10px] text-kaizen-dim2 flex-wrap">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-kaizen-violet inline-block" /> full</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-kaizen-violet/50 ring-2 ring-kaizen-violet/30 inline-block" /> floor</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full border border-white/10 inline-block" /> no pressure either way</span>
          </div>
        </div>

        {day1 && current && (
          <div className="glass-card rounded-3xl p-6 mt-4" data-testid="milestones-card">
            <div className="flex items-center gap-1.5 text-kaizen-dim mb-4">
              <Sparkles size={13} />
              <p className="text-xs uppercase tracking-widest">Since day 1</p>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="text-center flex-1" data-testid="milestone-day1">
                <p className="text-[10px] text-kaizen-dim2 uppercase tracking-widest">Day 1</p>
                <p className="font-display text-lg text-kaizen-text mt-1">{day1.total_sets} sets</p>
                <p className="text-xs text-kaizen-dim mt-0.5">{day1.exercise_count} moves</p>
              </div>
              <ArrowRight size={16} className="text-kaizen-violetSoft shrink-0" />
              <div className="text-center flex-1" data-testid="milestone-current">
                <p className="text-[10px] text-kaizen-dim2 uppercase tracking-widest">Now</p>
                <p className="font-display text-lg text-kaizen-violetSoft mt-1">{current.total_sets} sets</p>
                <p className="text-xs text-kaizen-dim mt-0.5">{current.exercise_count} moves</p>
              </div>
            </div>
            <p className="text-center text-xs text-kaizen-dim2 mt-4" data-testid="milestone-sessions-logged">
              {data.total_sessions_logged ?? 0} sessions logged, one small step at a time.
            </p>
          </div>
        )}

        <div className="glass-card rounded-3xl p-6 mt-4" data-testid="week-plan-card">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs uppercase tracking-widest text-kaizen-dim">This week's plan</p>
          </div>
          <AnimatePresence>
            {rebuildNote && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="font-display italic text-sm text-kaizen-violetSoft pb-3"
                data-testid="week-rebuild-confirmation"
              >
                {rebuildNote}
              </motion.p>
            )}
          </AnimatePresence>
          {(data.current_week_plan || []).length === 0 ? (
            <p className="text-sm text-kaizen-dim py-2" data-testid="week-plan-empty">
              Nova's still lining this week up — check back in a moment.
            </p>
          ) : (
            <div data-testid="week-session-list">
              {data.current_week_plan.map((s, i) => (
                <WeekSessionRow
                  key={i}
                  session={s}
                  idx={i}
                  expanded={expandedIdx === i}
                  onToggle={() => setExpandedIdx((cur) => (cur === i ? null : i))}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={() => runAction("reflow", setReflowing, "Rebalanced — nothing lost, nothing stacked.")}
            disabled={reflowing || regenerating}
            data-testid="reflow-remaining-days-button"
            className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full border border-white/15 text-xs text-kaizen-dim hover:border-kaizen-violetSoft/40 hover:text-kaizen-violetSoft transition-colors duration-300 disabled:opacity-50"
          >
            <RotateCcw size={13} className={reflowing ? "animate-spin" : ""} />
            {reflowing ? "Rebalancing…" : "Reflow remaining days"}
          </button>
          <button
            onClick={() => runAction("regenerate-week", setRegenerating, "Your week's been rebuilt.")}
            disabled={reflowing || regenerating}
            data-testid="regenerate-week-button"
            className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-full border border-white/15 text-xs text-kaizen-dim hover:border-kaizen-violetSoft/40 hover:text-kaizen-violetSoft transition-colors duration-300 disabled:opacity-50"
          >
            <Shuffle size={13} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Refreshing…" : "Regenerate week"}
          </button>
        </div>
      </div>
      <BottomNav />
    </div>
  );
}
